<template>
  <v-dialog :retain-focus="false" :value="sectionEditor.show" :width="1100" @input="handleClosePopup">
    <div style="background-color: #fff">
      <div class="header-modal">
        <v-card-title class="d-flex justify-space-between align-center" style="padding: 10px 0">
          <span style="color: #0081c7">{{ fields.title }}</span>
          <v-btn icon @click="handleClosePopup">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
      </div>
      <v-card-text>
        <div class="mb-4">
          <label class="label-input">Section Name</label>
          <v-text-field v-model="fields.title" hide-details />
        </div>

        <SectionLayout :showCustomWidth="false" :settings="fields.settings" :container-page="fields.containerPage" />

        <!-- Title Card -->
        <div class="mb-5">
          <label class="label-input">Title Section</label>
          <div class="group-item">
            <v-checkbox v-model="fields.cardTitle.show" label="Show title block" hide-details class="mt-0 mb-2" />
            <ckeditor
              v-if="fields.cardTitle.show"
              :editor="editor"
              v-model="fields.cardTitle.content"
              :config="editorConfig"
            />
          </div>
        </div>

        <!-- Slide items -->
        <div class="mb-5">
          <div class="d-flex justify-space-between align-center mb-2">
            <label class="label-input mb-0">Slides</label>
            <v-btn small color="primary" :disabled="fields.items.length >= MAX_SLIDE_ITEMS" @click="handleAddItem">
              <v-icon size="16">mdi-plus</v-icon>
              <span>Add Slide</span>
            </v-btn>
          </div>
          <div class="group-item">
            <v-expansion-panels accordion>
              <v-expansion-panel v-for="(item, idx) in fields.items" :key="`slide-item-${idx}`">
                <v-expansion-panel-header>
                  <div class="d-flex justify-space-between align-center" style="width: 100%">
                    <span>Slide {{ idx + 1 }} - {{ item.title || '' }}</span>
                    <v-btn icon :disabled="fields.items.length <= MIN_SLIDE_ITEMS" @click.stop="handleRemoveItem(idx)">
                      <v-icon size="20" color="#ff6c6c">mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <div class="sub-block">
                    <label>Image</label>
                    <UploadImage
                      :image="{ imageSrc: item.image }"
                      :height="`220`"
                      :typeUpload="'image'"
                      @callback="obj => (item.image = obj.imageSrc)"
                      @callbackDelete="item.image = ''"
                    />
                  </div>

                  <div class="sub-block">
                    <v-row>
                      <v-col cols="12" md="6">
                        <label>Sub Title</label>
                        <v-text-field v-model="item.subTitle" hide-details class="mt-0" />
                      </v-col>
                      <v-col cols="12" md="6">
                        <label>Title</label>
                        <v-text-field v-model="item.title" hide-details class="mt-0" />
                      </v-col>
                    </v-row>
                  </div>

                  <div class="sub-block">
                    <label class="mb-0 d-block">CTA</label>
                    <v-row>
                      <v-col cols="12" md="6">
                        <label>Text</label>
                        <v-text-field v-model="item.cta.text" hide-details class="mt-0" />
                      </v-col>
                      <v-col cols="12" md="6">
                        <label>Link</label>
                        <v-text-field v-model="item.cta.link" hide-details class="mt-0" />
                      </v-col>
                    </v-row>
                    <!-- Ảnh icon-cta là tuỳ chọn: xoá ảnh -> tag <img> bị loại khỏi DOM khi Save. -->
                    <label class="mt-3 d-block">Icon (tuỳ chọn - để trống sẽ không render icon)</label>
                    <UploadImage
                      :image="{ imageSrc: item.cta.icon }"
                      :height="`100`"
                      :typeUpload="'image'"
                      @callback="obj => (item.cta.icon = obj.imageSrc)"
                      @callbackDelete="item.cta.icon = ''"
                    />
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </div>
      </v-card-text>
      <div class="footer-modal">
        <v-card-actions style="padding: 10px 0">
          <v-spacer></v-spacer>
          <v-btn min-width="100" depressed @click="handleClosePopup">Cancel</v-btn>
          <v-btn min-width="100" color="primary" @click="handleSave">Save</v-btn>
        </v-card-actions>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import { cloneDeep } from 'lodash';
import Editor from 'ckeditor5-custom-build/build/ckeditor';
import { ckeditorConfigFullToolbar } from '@/helpers/editor';
import { updateHtmlFromData } from '../common/html/update';
import UploadImage from '../common/form/upload-file/upload-image.vue';
import SectionLayout from '../common/ui-modules/section-layout/editor.vue';

const MIN_SLIDE_ITEMS = 1;
const MAX_SLIDE_ITEMS = 12;

const buildSlideItemDefault = () => ({
  image: '',
  subTitle: 'Realign & Lift',
  title: 'Posture & Support',
  cta: { link: '#', text: 'Shop Now', icon: '' },
});

const getDefaultFields = () => ({
  title: 'Calyrafit Slide Section',
  settings: {
    paddingTop: true,
    paddingBottom: true,
    backgroundColor: '#FFFFFF',
    textColor: '#000000',
    overlayColor: '#000000',
    overlayOpacity: 0,
    showOverlay: false,
    backgroundImage: '',
    useCustomWidth: false,
    customWidth: 1204,
  },
  containerPage: {
    enableStyle: false,
    background: '#FFFFFF',
  },
  cardTitle: {
    show: true,
    content: "<h2>Don't just take our word for it</h2><p></p>",
  },
  items: [buildSlideItemDefault()],
});

export default {
  name: 'CalyrafitSlideSectionEditor',
  components: { UploadImage, SectionLayout },
  props: {
    sectionEditor: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      MIN_SLIDE_ITEMS,
      MAX_SLIDE_ITEMS,
      editor: Editor,
      editorConfig: ckeditorConfigFullToolbar,
      fields: getDefaultFields(),
    };
  },
  mounted() {
    const def = getDefaultFields();
    const extract = cloneDeep(this.sectionEditor.sectionExtract || {});

    this.fields = {
      ...def,
      ...extract,
      settings: { ...def.settings, ...(extract.settings || {}) },
      containerPage: { ...def.containerPage, ...(extract.containerPage || {}) },
      cardTitle: { ...def.cardTitle, ...(extract.cardTitle || {}) },
      items:
        Array.isArray(extract.items) && extract.items.length
          ? extract.items.map(item => ({
              ...buildSlideItemDefault(),
              ...item,
              cta: { ...buildSlideItemDefault().cta, ...(item.cta || {}) },
            }))
          : def.items,
    };
  },
  methods: {
    handleClosePopup() {
      this.$emit('close');
    },
    handleSave() {
      const updateHtml = updateHtmlFromData.sectionCalyrafitSlide(this.sectionEditor.section.html, this.fields);
      this.$emit('update:section', {
        ...this.sectionEditor.section,
        title: this.fields.title,
        html: updateHtml,
      });
      this.$emit('close');
    },
    handleAddItem() {
      if (this.fields.items.length >= MAX_SLIDE_ITEMS) return;
      this.fields.items.push(cloneDeep(buildSlideItemDefault()));
    },
    handleRemoveItem(idx) {
      if (this.fields.items.length <= MIN_SLIDE_ITEMS) return;
      this.fields.items.splice(idx, 1);
    },
  },
};
</script>

<style lang="scss" scoped>
.group-item {
  border: 1px solid #0000000f;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 16px;
}
.sub-block {
  border-top: 1px dashed #0000001a;
  padding-top: 12px;
  margin-top: 12px;
}
.ck-editor__editable_inline,
.ck-editor__editable {
  min-height: 120px;
}
.label-input {
  font-size: 18px !important;
  font-weight: 500;
  color: #000;
  margin-bottom: 10px;
  display: block;
}
.header-modal {
  position: sticky;
  top: 0;
  left: 0;
  z-index: 999;
  background-color: #fff;
  padding: 0 16px;
  box-shadow: 0px 0px 12px #0000000f;
}
.footer-modal {
  position: sticky;
  padding: 0 16px;
  bottom: 0;
  left: 0;
  z-index: 999;
  background-color: #fff;
  box-shadow: 0px 0px 12px #0000000f;
}
</style>
